/* Route tables export config, not components — fast refresh does not apply here. */
/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import { Cookies } from 'react-cookie';
import {
  LoaderFunctionArgs,
  Navigate,
  Params,
  RouteObject,
  ShouldRevalidateFunction,
  createPath,
  generatePath,
  redirect,
} from 'react-router';
import OnLoginClientsSelectorHandler from '~/components/Clients/OnLoginClientsSelector/OnLoginClientsSelector';
import AccountLayout from '~/layouts/Admin/Admin';
import SessionHandler from '~/views/Dashboard/SessionHandler';
import ErrorPage from '~/views/Pages/ErrorPage';
import App from './App';
import RoutingHandler from './RoutingHandler';
import {
  adManagerDataQueryFn,
  adManagerQueryKeys,
} from './hooks/queries/useAdmanagerData/useAdManagerData';
import { analyticsStatMetadataQueryOptions } from './hooks/queries/useAnalyticsStatMetadataQuery';
import { getAnalyticsStatSchedulesQueryOptions } from './hooks/queries/useAnalyticsStatSchedulesQuery';
import { getAnalyticsStatsListQueryOptions } from './hooks/queries/useAnalyticsStatsListQuery';
import { getAnalyticsStatsViewsQueryOptions } from './hooks/queries/useAnalyticsStatsViewsQuery';
import { getAudiencesQueryOptions } from './hooks/queries/useAudienceData';
import { getAudiencePreferenceQueryOptions } from './hooks/queries/useAudiencePreferenceQuery/useAudiencePreferenceQuery';
import { getClientPlQueryOptions } from './hooks/queries/useClientPlQuery';
import { getCohortsQueryOptions } from './hooks/queries/useCohortQuery/useCohortQuery';
import { getDomainManagerQueryOptions } from './hooks/queries/useDomainManagerQuery/useDomainManagerQuery';
import { prefetchAnalyticsEntityFilterQueries } from './hooks/queries/useEntityFilterQueries';
import { getGenieNavQueryOptions } from './hooks/queries/useGenieNavQuery';
import { countriesQueryOptions } from './hooks/queries/useGeoDataQuery';
import { getOsTargetsQueryOptions } from './hooks/queries/useOsTargetsQuery';
import { getPartnersQueryOptions } from './hooks/queries/usePartnerData/usePartnerData';
import {
  ResolvedClientSessionData,
  ResolvedPartnerSessionData,
} from './hooks/queries/useSessionPlQuery';
import lazy from './lazy';
import { globalClient } from './query-client';
import { automatedReportingLoader } from './routing/automatedReportingLoader';
import { perms } from './routing/perms';
import { prefetchColumnTooltipsLoader } from './routing/prefetchColumnTooltipsData';
import { prefetchStaffReportTemplatesLoader } from './routing/prefetchStaffReportTemplatesData';
import { requirePerm } from './routing/requirePerm';
import { resolveSessionMiddleware } from './routing/resolveSessionMiddleware';
import { resolveStaffSessionMiddleware } from './routing/resolveStaffSessionMiddleware';
import { prefetchStaffAnalyticsUpdateRequestLoader } from './routing/prefetchStaffAnalyticsUpdateRequestData';
import { staffClientPermsLoader } from './routing/staffClientPermsLoader';
import { getAnalyticsReportingMode, usesAnalyticsStatsUi } from './utils/analyticsReportingMode';
import { customFetch } from './utils/customFetch';
import {
  getClientIASRulesOptions,
  getClientIASSegmentsOptions,
} from './views/Dashboard/AccountParameters/IAS/hooks';
import { getDefaultStat } from './views/Dashboard/Analytics/utils';
import { parentChildColumnAccessChildren } from './views/Dashboard/ParentStatAccess/routes';
import SetupMFAPage from './views/Dashboard/SetupMFAPage';
import { audiencePreferencesToggleStorageKey } from './views/Dashboard/ad-form/audience-targeting/audience.utils';
import {
  getCampaignGoalOptionsQueryOptions,
  getCampaignPlanByIdQueryOptions,
  getCampaignPlansQueryOptions,
} from './views/Dashboard/campaign-planner/hooks';
import {
  INVENTORY_PUBLISHER_BIDDING_PATH,
  INVENTORY_SUPPLY_MANAGER_PATH,
  supplyManagerIndexLoader,
  supplyManagerLoader,
} from './views/Dashboard/supply-manager/supplyManagerRouteLoaders';
import ErrorBoundaryPage from './views/Pages/ErrorBoundaryPage';
import MfaPage from './views/Pages/MFAPage';
import NoSessionPage from './views/Pages/NoSessionPage';

const PreviewsPage = lazy(() => import('./views/Pages/preview'));
const VerificationPreviewPage = lazy(() => import('./views/Pages/verification-preview'));
const RepricingVoiding = lazy(() => import('~/views/Staff/RepricingVoiding'));
const RepricingVoidingStatus = lazy(() => import('~/views/Staff/RepricingVoidingStatus'));
const VoidRepriceForm = lazy(() => import('~/views/Staff/VoidRepriceForm/VoidRepriceForm'));
const VoidRepriceManagerTable = lazy(
  () => import('~/views/Staff/VoidRepriceManager/VoidRepriceManagerTable'),
);
const VoidRepriceControlPanel = lazy(
  () => import('~/views/Staff/VoidRepriceControlPanel/VoidRepriceControlPanel'),
);
const EditingAdFormHandler = lazy(() => import('~/views/Dashboard/ad-form/editing-adform'));
const CreatingAdFormHandler = lazy(() => import('~/views/Dashboard/ad-form/creating-adform'));
const NewClient = lazy(() => import('~/views/Dashboard/NewClient'));
const AdManager = lazy(() => import('~/views/Dashboard/AdManager'));
const Insights = lazy(() => import('~/views/Dashboard/Insights'));
const Billing = lazy(() => import('~/views/Dashboard/Billing/Billing'));
const InvoiceRoute = lazy(() => import('~/views/Dashboard/Billing/Invoice'));
const PublisherAds = lazy(() => import('~/views/Dashboard/PublisherAds'));
const PublisherPaymentHistory = lazy(() => import('~/views/Dashboard/PublisherPaymentHistory'));
const AccountUsers = lazy(() => import('~/views/Dashboard/Account/AccountUsers'));
const AccountSettings = lazy(() => import('~/views/Dashboard/Account/AccountSettings'));
const ChildAccountsPage = lazy(() => import('~/views/Dashboard/ChildAccounts/ChildAccountsPage'));
const UserProfile = lazy(() => import('~/views/Dashboard/UserProfile'));
const ConversionTrackers = lazy(
  () => import('~/views/Dashboard/ConversionTrackers/ConversionTrackerManager'),
);
const ArchivedAccountsPage = lazy(() => import('~/views/Pages/ArchivedAccountsPage'));
const Analytics = lazy(() => import('~/views/Dashboard/Analytics'));
const AnalyticsStats = lazy(() => import('~/views/Dashboard/AnalyticsStats'));
const AnalyticsStatsHome = lazy(
  () => import('~/views/Dashboard/AnalyticsStats/AnalyticsStatsHome'),
);
const AnalyticsStatsBuilder = lazy(
  () => import('~/views/Dashboard/AnalyticsStats/AnalyticsStatsBuilder'),
);
const SavedViewResultsPage = lazy(
  () => import('~/views/Dashboard/AnalyticsStats/SavedViewResultsPage'),
);
const StatSchedulesPage = lazy(() => import('~/views/Dashboard/AnalyticsStats/StatSchedulesPage'));
const StatScheduleFormPage = lazy(
  () => import('~/views/Dashboard/AnalyticsStats/StatScheduleFormPage'),
);
const ApiAccessHubPage = lazy(() => import('~/views/Dashboard/AnalyticsStats/ApiAccessHubPage'));
const AnalyticsStatJobEmailResultsPage = lazy(
  () => import('~/views/Dashboard/AnalyticsStats/AnalyticsStatJobEmailResultsPage'),
);
const ForbiddenPage = lazy(() => import('~/views/Pages/ForbiddenPage'));
const OverviewHandler = lazy(() => import('~/views/Dashboard/Overview/OverviewHandler'));
const DeviceBidManager = lazy(() => import('~/views/Dashboard/DeviceBidManager'));
const AccountParameters = lazy(
  () => import('~/views/Dashboard/AccountParameters/AccountParameters'),
);
const AccountLevelRules = lazy(
  () => import('~/views/Dashboard/AccountParameters/AccountLevelRules'),
);
const CreateCapPage = lazy(() => import('~/views/Dashboard/AdManager/OverallCaps/CreateCapPage'));
const StaffLayout = lazy(() => import('./layouts/Staff/StaffLayout'));
const AuthLayout = lazy(() => import('./layouts/Auth/Auth'));
const UserProfileLayout = lazy(() => import('./layouts/UserProfile/UserProfileLayout'));
const LoginPage = lazy(() => import('./views/Pages/LoginPage'));
const SetPassword = lazy(() => import('./views/Pages/SetPassword'));
const Invite = lazy(() => import('./views/Pages/Signup/Invite'));
const UserFormHandler = lazy(() => import('./views/Pages/Signup/UserForm'));
const SignUpCompletePage = lazy(() => import('./views/Pages/Signup/SignUpCompletePage'));
const StepLayout = lazy(() => import('./views/Pages/Signup/StepLayout'));
const AutomatedReporting = lazy(() => import('./views/Dashboard/AutomatedReporting'));
const AutoReportFormWrapper = lazy(
  () => import('./views/Dashboard/AutomatedReporting/AutoReportFormWrapper'),
);
const StaffDashboard = lazy(() => import('./views/Staff/StaffDashboard'));
const StaffClientManagerLayout = lazy(
  () => import('./views/Staff/StatLimits/StaffClientManagerLayout'),
);
const StaffClientHomepage = lazy(() => import('./views/Staff/StatLimits/StaffClientHomepage'));
const ColumnTooltipsPage = lazy(
  () => import('./views/Staff/AnalyticsColumnTooltips/ColumnTooltipsPage'),
);
const RequestAnalyticsUpdatePage = lazy(
  () => import('./views/Staff/RequestAnalyticsUpdate/RequestAnalyticsUpdatePage'),
);
const ReportTemplatesListPage = lazy(
  () => import('./views/Staff/ReportTemplates/ReportTemplatesListPage'),
);
const ReportTemplateEditorPage = lazy(
  () => import('./views/Staff/ReportTemplates/ReportTemplateEditorPage'),
);
const InviteClient = lazy(() => import('./views/Staff/InviteClient/InviteClient'));
const ManageInvites = lazy(() => import('./views/Staff/ManageInvites/ManageInvites'));
const UploadSignupIOWrapper = lazy(() => import('./views/Staff/UploadSignupIO/UploadSignupIO'));
const Legal = lazy(() => import('./views/Legal/Legal'));
const LegalType = lazy(() => import('./views/Legal/LegalType'));
const ClientDetails = lazy(() => import('./views/Pages/Signup/ClientForm'));
const BillingDetails = lazy(() => import('./views/Pages/Signup/BillingForm'));
const ReviewStep = lazy(() => import('./views/Pages/Signup/ReviewStep'));
const AudienceManager = lazy(() => import('./views/Dashboard/Audience/audience-manager'));
const SupplyManager = lazy(() => import('./views/Dashboard/supply-manager'));
const ContextfeedManager = lazy(() => import('./views/Dashboard/Contextfeed/contextfeed-manager'));
const MFARecoveryPage = lazy(() => import('./views/Pages/MFARecovery'));
const CampaignPlanner = lazy(() => import('./views/Dashboard/campaign-planner/campaign-planner'));
const DisplayClickTester = lazy(() => import('./views/Pages/DisplayClickTester'));
const PollManager = lazy(() => import('./views/Dashboard/Polls/PollManager'));
const DomainManager = lazy(() => import('./views/Dashboard/DomainManager/DomainManager'));
const IASSegmentRoute = lazy(
  () => import('./views/Dashboard/AccountParameters/IAS/EditIASRulesRoute'),
);
const GettingStarted = lazy(() => import('./views/Dashboard/getting-started/getting-started'));
const AccountEntryFallback = lazy(() => import('./views/Pages/AccountEntryFallback'));
const CeltraReports = lazy(() => import('./views/Dashboard/Analytics/CeltraReports/CeltraReports'));
const DappierReports = lazy(
  () => import('./views/Dashboard/Analytics/DappierReports/DappierReports'),
);

const analyticsLoader = async (ctx: LoaderFunctionArgs) => {
  const sessionData = await requirePerm(perms.analytics)(ctx);
  const genieNavData = await globalClient.ensureQueryData(getGenieNavQueryOptions({ sessionData }));
  return { sessionData, genieNavData };
};

const getRedirectPath = (sessionPlData: ResolvedPartnerSessionData | ResolvedClientSessionData) => {
  const accountPath = `/${sessionPlData?.accountType}/${sessionPlData?.id}`;

  if (perms.analytics({ sessionPlData })) return `${accountPath}/dashboard`;
  if (perms.adManager({ sessionPlData })) return `${accountPath}/ad-manager`;
  if (
    perms.insights({ sessionPlData }) &&
    Array.isArray(sessionPlData.insights_nav) &&
    sessionPlData.insights_nav.length
  ) {
    const { path } = sessionPlData.insights_nav.sort((a, b) => a.path.localeCompare(b.path))[0];
    return `${accountPath}/insights/${path}`;
  }
  if (perms.billing({ sessionPlData })) return `${accountPath}/billing`;
  return `${accountPath}/account-entry-fallback`;
};

const accountEntryPointLoader = async (ctx: LoaderFunctionArgs) => {
  const sessionData = await requirePerm(perms.loggedIn)(ctx);
  const url = new URL(ctx.request.url);
  const noSessionReferer =
    url.pathname !== '/no-session'
      ? new URLSearchParams({ referer: url.pathname + url.search }).toString()
      : '';

  if (!sessionData) {
    throw redirect(`/no-session?${noSessionReferer}`);
  }
  const redirectPath = getRedirectPath(sessionData);
  return redirect(redirectPath);
};

const ignoreSearchParams: ShouldRevalidateFunction = ({ currentUrl, nextUrl }) => {
  const currentPathname = currentUrl.pathname;
  const nextPathname = nextUrl.pathname;
  return currentPathname !== nextPathname;
};

/** Build a redirect preserving existing search params, with optional extras. */
const redirectWithQueryParams = ({
  to,
  params,
  request,
  queryParams,
}: {
  to: string;
  params: Params;
  request: Request;
  queryParams?: Record<string, string | undefined | null>;
}) => {
  const searchParams = new URLSearchParams(new URL(request.url).search);
  Object.entries(queryParams ?? {}).forEach(([key, value]) => {
    if (value != null) {
      searchParams.set(key, value);
    }
  });

  return redirect(
    createPath({
      pathname: generatePath(to, params),
      search: searchParams.toString(),
    }),
  );
};

const prefetchAdManagerData = async (ctx: LoaderFunctionArgs) => {
  if (!ctx.params.accountId) {
    return null;
  }
  // prefill cache with lots of data
  globalClient.prefetchQuery({
    queryKey: adManagerQueryKeys.all({ clientId: ctx.params.accountId }),
    queryFn: () => adManagerDataQueryFn(Number(ctx.params.accountId)),
  });
  globalClient.prefetchQuery(getPartnersQueryOptions(ctx.params.accountId));
  globalClient.prefetchQuery(getCohortsQueryOptions(Number(ctx.params.accountId)));
  globalClient.prefetchQuery(getOsTargetsQueryOptions());
  globalClient.prefetchQuery(getAudiencesQueryOptions());

  return null;
};

const prefetchGenericAdFormData = async (ctx: LoaderFunctionArgs) => {
  let starredOnly = false;
  try {
    starredOnly = Boolean(localStorage.getItem(audiencePreferencesToggleStorageKey)) ?? false;
  } catch {
    starredOnly = false;
  }

  if (!ctx.params.accountId) {
    return null;
  }
  globalClient.prefetchQuery({
    queryKey: adManagerQueryKeys.single({
      clientId: ctx.params.accountId,
      productId: Number(ctx.params.campaignId),
    }),
    queryFn: () =>
      adManagerDataQueryFn(Number(ctx.params.accountId), Number(ctx.params.campaignId)),
  });
  globalClient.prefetchQuery(getPartnersQueryOptions(ctx.params.accountId));
  globalClient.prefetchQuery(getCohortsQueryOptions(Number(ctx.params.accountId), starredOnly));
  globalClient.prefetchQuery(getOsTargetsQueryOptions());
  globalClient.prefetchQuery(getAudiencesQueryOptions());
  globalClient.prefetchQuery(getClientPlQueryOptions(Number(ctx.params.accountId)));

  return null;
};

const prefetchAnalyticsStatsData = async (ctx: LoaderFunctionArgs) => {
  const { accountId, accountType } = ctx.params;
  if (!accountId || !accountType) return null;

  const accountTypeValid = accountType === 'client' || accountType === 'partner';
  if (!accountTypeValid) return null;

  const id = Number(accountId);
  globalClient.prefetchQuery(getAnalyticsStatsListQueryOptions(id, accountType));
  globalClient.prefetchQuery(getAnalyticsStatsViewsQueryOptions(id, accountType));

  return null;
};

const prefetchAnalyticsStatSchedulesAll = async (ctx: LoaderFunctionArgs) => {
  const { accountId, accountType } = ctx.params;
  if (!accountId || !accountType) return null;
  const accountTypeValid = accountType === 'client' || accountType === 'partner';
  if (!accountTypeValid) return null;
  const id = Number(accountId);
  await globalClient.prefetchQuery(
    getAnalyticsStatSchedulesQueryOptions(id, accountType, undefined),
  );
  return null;
};

const prefetchAnalyticsStatSchedulesForView = async (ctx: LoaderFunctionArgs) => {
  const { accountId, accountType, viewPublicId } = ctx.params;
  if (!accountId || !accountType || !viewPublicId) return null;
  const accountTypeValid = accountType === 'client' || accountType === 'partner';
  if (!accountTypeValid) return null;
  const id = Number(accountId);
  await prefetchAnalyticsStatsData(ctx);
  const viewsData = await globalClient.ensureQueryData(
    getAnalyticsStatsViewsQueryOptions(id, accountType),
  );
  const view = viewsData?.views?.find((v) => v.publicId === viewPublicId);
  if (view?.statKey) {
    await globalClient.prefetchQuery(
      getAnalyticsStatSchedulesQueryOptions(id, accountType, view.statKey),
    );
  }
  return null;
};

const prefetchAnalyticsStatsBuilderData = async (ctx: LoaderFunctionArgs) => {
  const { accountId, accountType, statKey, viewPublicId } = ctx.params;
  if (!accountId || !accountType) return null;

  const accountTypeValid = accountType === 'client' || accountType === 'partner';
  if (!accountTypeValid) return null;

  const id = Number(accountId);

  if (statKey) {
    globalClient.prefetchQuery(analyticsStatMetadataQueryOptions(id, accountType, statKey));
  } else if (viewPublicId) {
    const viewsData = await globalClient.ensureQueryData(
      getAnalyticsStatsViewsQueryOptions(id, accountType),
    );
    const view = viewsData?.views?.find((v) => v.publicId === viewPublicId);
    if (view?.statKey) {
      await globalClient.prefetchQuery(
        analyticsStatMetadataQueryOptions(id, accountType, view.statKey),
      );
    }
  }

  const clientPl = await globalClient.ensureQueryData(getClientPlQueryOptions(id));
  const { search } = new URL(ctx.request.url);
  await prefetchAnalyticsEntityFilterQueries(globalClient, {
    accountId: id,
    search,
    isParent: !!clientPl?.isParent,
  });

  return null;
};

export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorBoundaryPage />,
    children: [
      {
        index: true,
        element: <RoutingHandler />,
      },
      {
        path: '/:accountType/:accountId/previews/:publisherId?/:productId/:adgroupId/:variantId',
        // this component is using casting in params so if you change things
        // here things in the component _WILL_ break
        element: <PreviewsPage />,
        middleware: [resolveSessionMiddleware(globalClient)],
        loader: requirePerm(perms.loggedIn),
      },
      {
        path: '/:accountType/:accountId/previews/:productId/verification/:productPromotionId',
        // this component is using casting in params so if you change things
        // here things in the component _WILL_ break
        element: <VerificationPreviewPage />,
        middleware: [resolveSessionMiddleware(globalClient)],
        loader: requirePerm(perms.loggedIn),
      },
      {
        element: <AccountLayout />,
        children: [
          {
            path: 'select-account',
            element: <OnLoginClientsSelectorHandler />,
            middleware: [resolveSessionMiddleware(globalClient)],
            loader: requirePerm(perms.loggedIn),
          },
          {
            path: ':accountType/:accountId',
            element: <SessionHandler />,
            middleware: [resolveSessionMiddleware(globalClient)],
            children: [
              {
                index: true,
                loader: accountEntryPointLoader,
              },
              {
                path: 'dashboard',
                loader: async (ctx) => {
                  await requirePerm(perms.overview)(ctx);
                  const { accountType, accountId } = ctx.params;
                  if (accountType === 'client') {
                    // await it to ensure it's fetched before the
                    // overviewHandler is rendered so we don't make unnecessary
                    // calls to the wrong stats on load
                    await globalClient.prefetchQuery(getClientPlQueryOptions(Number(accountId)));
                  }
                  return null;
                },
                element: <OverviewHandler />,
              },
              {
                path: 'account',
                shouldRevalidate: ignoreSearchParams,
                children: [
                  {
                    index: true,
                    element: <Navigate to="parameters" />,
                  },
                  {
                    path: 'parameters',
                    element: <AccountParameters />,
                    loader: requirePerm(perms.bookingsManager),
                    shouldRevalidate: ignoreSearchParams,
                  },
                  {
                    path: 'parameters/create-booking',
                    element: <CreateCapPage />,
                    loader: requirePerm(perms.bookingsManager),
                    shouldRevalidate: ignoreSearchParams,
                  },
                  {
                    path: 'rules',
                    children: [
                      {
                        index: true,
                        element: <AccountLevelRules />,
                        loader: async (ctx) => {
                          const sessionData = await requirePerm(perms.accountRules)(ctx);
                          const clientPlQuery = await globalClient.ensureQueryData(
                            getClientPlQueryOptions(Number(ctx.params.accountId)),
                          );
                          const allowIasBrandSafety =
                            !!clientPlQuery?.brandSafetyPermissions.ias ||
                            !!sessionData?.login.is_dianomi;
                          if (allowIasBrandSafety) {
                            // not awaiting as they are not required for the page to load
                            globalClient.prefetchQuery(
                              getClientIASRulesOptions(Number(ctx.params.accountId)),
                            );
                            globalClient.prefetchQuery(
                              getClientIASSegmentsOptions(Number(ctx.params.accountId)),
                            );
                          }
                          return null;
                        },
                      },
                      {
                        path: 'ias',
                        element: <IASSegmentRoute />,
                        loader: async (ctx) => {
                          const sessionData = await requirePerm(perms.accountRules)(ctx);
                          const clientPlQuery = await globalClient.ensureQueryData(
                            getClientPlQueryOptions(Number(ctx.params.accountId)),
                          );
                          const allowIasBrandSafety =
                            !!clientPlQuery?.brandSafetyPermissions.ias ||
                            !!sessionData?.login.is_dianomi;
                          if (!allowIasBrandSafety) {
                            throw redirect(
                              `/${ctx.params.accountType}/${ctx.params.accountId}/rules`,
                            );
                          }
                          const [rules, segments] = await Promise.all([
                            globalClient.ensureQueryData(
                              getClientIASRulesOptions(Number(ctx.params.accountId)),
                            ),
                            globalClient.prefetchQuery(
                              getClientIASSegmentsOptions(Number(ctx.params.accountId)),
                            ),
                          ]);
                          return { rules, segments };
                        },
                      },
                    ],
                  },
                  // TODO: Inventory Management - Remove this route after cooling off period
                  {
                    path: 'publisher-manager/bid',
                    children: [
                      {
                        index: true,
                        loader: (ctx) => {
                          throw redirectWithQueryParams({
                            to: INVENTORY_PUBLISHER_BIDDING_PATH,
                            params: ctx.params,
                            request: ctx.request,
                          });
                        },
                      },
                      {
                        path: ':campaignId',
                        loader: (ctx) => {
                          throw redirectWithQueryParams({
                            to: INVENTORY_PUBLISHER_BIDDING_PATH,
                            params: ctx.params,
                            request: ctx.request,
                            queryParams: { campaign: ctx.params.campaignId },
                          });
                        },
                      },
                    ],
                  },
                  // TODO: Inventory Management - Remove this route after cooling off period
                  {
                    path: 'device-manager/bid',
                    children: [
                      {
                        index: true,
                        element: <DeviceBidManager />,
                        loader: (ctx) => {
                          throw redirectWithQueryParams({
                            to: '/:accountType/:accountId/inventory/device-manager/bid',
                            params: ctx.params,
                            request: ctx.request,
                          });
                        },
                      },
                      {
                        path: ':campaignId',
                        element: <DeviceBidManager />,
                        loader: (ctx) => {
                          throw redirectWithQueryParams({
                            to: '/:accountType/:accountId/inventory/device-manager/bid/:campaignId',
                            params: ctx.params,
                            request: ctx.request,
                          });
                        },
                      },
                    ],
                  },
                  {
                    path: 'actions',
                    element: <ConversionTrackers />,
                    loader: requirePerm(perms.conversionTrackers),
                  },
                  {
                    path: 'users',
                    element: <AccountUsers />,
                    loader: requirePerm(perms.accountUsers),
                  },
                  {
                    path: 'child-accounts',
                    loader: async (ctx) => {
                      await requirePerm(perms.childAccountsHub)(ctx);
                      if (ctx.params.accountId) {
                        globalClient.prefetchQuery(
                          getClientPlQueryOptions(Number(ctx.params.accountId)),
                        );
                      }
                      return null;
                    },
                    children: [
                      {
                        index: true,
                        element: <ChildAccountsPage />,
                      },
                      {
                        path: 'new',
                        element: <NewClient />,
                        loader: requirePerm(perms.newClient),
                      },
                      ...parentChildColumnAccessChildren,
                    ],
                  },
                  {
                    path: 'profile',
                    element: <AccountSettings />,
                    loader: requirePerm(perms.accountSettings),
                  },
                  {
                    path: 'automated-reporting',
                    loader: automatedReportingLoader(globalClient),
                    children: [
                      {
                        index: true,
                        element: <AutomatedReporting />,
                      },
                      {
                        path: ':autoReportId',
                        element: <AutoReportFormWrapper />,
                      },
                      {
                        path: 'new',
                        element: <AutoReportFormWrapper />,
                      },
                    ],
                  },
                  {
                    path: 'audience-manager',
                    element: <AudienceManager />,
                    loader: async (ctx) => {
                      await requirePerm(perms.audienceManager)(ctx);
                      globalClient.prefetchQuery(
                        getCohortsQueryOptions(Number(ctx.params.accountId)),
                      );
                      globalClient.prefetchQuery(
                        getAudiencePreferenceQueryOptions(Number(ctx.params.accountId)),
                      );
                      return null;
                    },
                  },
                  // TODO: Inventory Management - Remove this route after cooling off period
                  {
                    path: 'supply-manager',
                    element: <SupplyManager />,
                    loader: (ctx) => {
                      throw redirectWithQueryParams({
                        to: INVENTORY_SUPPLY_MANAGER_PATH,
                        params: ctx.params,
                        request: ctx.request,
                      });
                    },
                  },
                  {
                    path: 'campaign-planner',
                    element: <CampaignPlanner />,
                    loader: async (ctx) => {
                      await requirePerm(perms.loggedIn)(ctx);
                      globalClient.prefetchQuery(
                        getCohortsQueryOptions(Number(ctx.params.accountId)),
                      );
                      globalClient.prefetchQuery(countriesQueryOptions);
                      globalClient.prefetchQuery(
                        getCampaignGoalOptionsQueryOptions(Number(ctx.params.accountId)),
                      );
                      const plans = await globalClient.ensureQueryData(
                        getCampaignPlansQueryOptions(Number(ctx.params.accountId)),
                      );
                      if (plans) {
                        for (const plan of plans) {
                          globalClient.setQueriesData(
                            getCampaignPlanByIdQueryOptions(
                              plan.externalId,
                              Number(ctx.params.accountId),
                            ),
                            () => plan,
                          );
                        }
                      }
                      return null;
                    },
                    handle: {
                      lazyChildren: () =>
                        import('~/views/Dashboard/campaign-planner/routes').then((m) => m.children),
                    },
                  },
                  // TODO: Inventory Management - Remove this route after cooling off period
                  {
                    path: 'domain-manager',
                    element: <DomainManager />,
                    loader: (ctx) => {
                      throw redirectWithQueryParams({
                        to: '/:accountType/:accountId/inventory/domain-manager',
                        params: ctx.params,
                        request: ctx.request,
                      });
                    },
                  },
                ],
              },
              {
                path: 'inventory',
                shouldRevalidate: ignoreSearchParams,
                children: [
                  {
                    path: 'device-manager/bid',
                    children: [
                      {
                        index: true,
                        element: <DeviceBidManager />,
                        loader: requirePerm(perms.deviceBidManagerClient),
                      },
                      {
                        path: ':campaignId',
                        element: <DeviceBidManager />,
                        loader: requirePerm(perms.deviceBidManagerProduct),
                      },
                    ],
                  },
                  {
                    path: 'domain-manager',
                    element: <DomainManager />,
                    loader: async (ctx) => {
                      await requirePerm(perms.domainManager)(ctx);
                      globalClient.prefetchQuery(
                        getDomainManagerQueryOptions(Number(ctx.params.accountId)),
                      );
                      return null;
                    },
                  },
                  {
                    path: 'supply-manager',
                    children: [
                      {
                        index: true,
                        element: <SupplyManager />,
                        loader: supplyManagerIndexLoader,
                      },
                      {
                        path: 'publisher-bidding',
                        element: <SupplyManager />,
                        loader: supplyManagerLoader,
                      },
                    ],
                  },
                ],
              },
              // Routes specifically for partners which sit in a different position to clients
              {
                path: 'automated-reporting',
                loader: automatedReportingLoader(globalClient),
                children: [
                  {
                    index: true,
                    element: <AutomatedReporting />,
                  },
                  {
                    path: ':autoReportId',
                    element: <AutoReportFormWrapper />,
                  },
                  {
                    path: 'new',
                    element: <AutoReportFormWrapper />,
                  },
                ],
              },
              {
                path: 'users',
                element: <AccountUsers />,
                loader: analyticsLoader,
              },
              {
                path: 'contextfeed-manager',
                element: <ContextfeedManager />,
                loader: async (ctx) => {
                  const [loader] = await Promise.all([
                    import('./views/Dashboard/Contextfeed/manager-loader').then(
                      (route) => route.contextfeedManagerLoader,
                    ),
                    requirePerm(perms.contextfeedManager)(ctx),
                  ]);

                  return loader(ctx, globalClient);
                },
                shouldRevalidate: ignoreSearchParams,
              },
              // Duplicate Partner routes end here
              {
                path: 'ad-manager',
                children: [
                  {
                    index: true,
                    element: <AdManager />,
                    loader: async (ctx) => {
                      await requirePerm(perms.adManager)(ctx);
                      return prefetchAdManagerData(ctx);
                    },
                    shouldRevalidate: ignoreSearchParams,
                  },
                  {
                    path: ':campaignId',
                    children: [
                      {
                        path: 'edit',
                        element: <EditingAdFormHandler />,
                        loader: async (ctx) => {
                          if (!ctx.params.campaignId) {
                            throw redirect(
                              `/${ctx.params.accountType}/${ctx.params.accountId}/ad-manager`,
                            );
                          }
                          await requirePerm(perms.adManager)(ctx);
                          return prefetchGenericAdFormData(ctx);
                        },
                        shouldRevalidate: ignoreSearchParams,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'new-ad',
                element: <Navigate to="native" />,
              },
              {
                path: 'new-ad/:type',
                element: <CreatingAdFormHandler />,
                loader: async (ctx) => {
                  if (ctx.params.type && ctx.params.type === 'poll') {
                    await requirePerm(perms.polls)(ctx);
                    return prefetchGenericAdFormData(ctx);
                  }
                  await requirePerm(perms.adManager)(ctx);
                  return prefetchGenericAdFormData(ctx);
                },
                shouldRevalidate: ignoreSearchParams,
              },
              {
                path: 'display-click-tester',
                element: <DisplayClickTester />,
                loader: requirePerm(perms.adManager),
              },
              {
                path: 'my-leads',
                element: <Analytics />,
                loader: analyticsLoader,
              },
              {
                path: 'analytics',
                loader: async (ctx) => {
                  const { genieNavData, sessionData } = await analyticsLoader(ctx);
                  if (!sessionData) {
                    throw redirect('/login');
                  }
                  const defaultStat = getDefaultStat(genieNavData);
                  const basePath = `/${sessionData.accountType}/${sessionData.id}/analytics`;
                  const reportingMode = getAnalyticsReportingMode(sessionData);
                  if (usesAnalyticsStatsUi(reportingMode) || !defaultStat) {
                    throw redirect(`${basePath}/stats`);
                  }
                  throw redirect(`${basePath}/${defaultStat.shortcode}`);
                },
              },
              {
                path: 'analytics/stats',
                element: <AnalyticsStats />,
                children: [
                  {
                    index: true,
                    element: <AnalyticsStatsHome />,
                    loader: async (ctx) => {
                      await requirePerm(perms.analyticsStats)(ctx);
                      return prefetchAnalyticsStatsData(ctx);
                    },
                  },
                  {
                    path: 'schedules/:viewPublicId/edit/:schedulePublicId',
                    element: <StatScheduleFormPage />,
                    loader: async (ctx) => {
                      await requirePerm(perms.analyticsStats)(ctx);
                      await prefetchAnalyticsStatsData(ctx);
                      return prefetchAnalyticsStatSchedulesForView(ctx);
                    },
                  },
                  {
                    path: 'schedules/:viewPublicId/new',
                    element: <StatScheduleFormPage />,
                    loader: async (ctx) => {
                      await requirePerm(perms.analyticsStats)(ctx);
                      await prefetchAnalyticsStatsData(ctx);
                      return prefetchAnalyticsStatSchedulesForView(ctx);
                    },
                  },
                  {
                    path: 'schedules/:viewPublicId?',
                    element: <StatSchedulesPage />,
                    loader: async (ctx) => {
                      await requirePerm(perms.analyticsStats)(ctx);
                      await prefetchAnalyticsStatsData(ctx);
                      if (ctx.params.viewPublicId) {
                        return prefetchAnalyticsStatSchedulesForView(ctx);
                      }
                      return prefetchAnalyticsStatSchedulesAll(ctx);
                    },
                  },
                  {
                    path: 'api',
                    element: <ApiAccessHubPage />,
                    loader: async (ctx) => {
                      await requirePerm(perms.analyticsStats)(ctx);
                      return prefetchAnalyticsStatsData(ctx);
                    },
                  },
                  {
                    path: 'view/:viewPublicId',
                    element: <SavedViewResultsPage />,
                    loader: async (ctx) => {
                      await requirePerm(perms.analyticsStats)(ctx);
                      return prefetchAnalyticsStatsBuilderData(ctx);
                    },
                    shouldRevalidate: ignoreSearchParams,
                  },
                  {
                    path: 'edit/:viewPublicId',
                    element: <AnalyticsStatsBuilder />,
                    loader: async (ctx) => {
                      await requirePerm(perms.analyticsStats)(ctx);
                      return prefetchAnalyticsStatsBuilderData(ctx);
                    },
                  },
                  {
                    path: 'new/:statKey',
                    element: <AnalyticsStatsBuilder />,
                    loader: async (ctx) => {
                      await requirePerm(perms.analyticsStats)(ctx);
                      return prefetchAnalyticsStatsBuilderData(ctx);
                    },
                    shouldRevalidate: ignoreSearchParams,
                  },
                ],
              },
              {
                path: 'analytics/job/:jobPublicId',
                element: <AnalyticsStatJobEmailResultsPage />,
                loader: async (ctx) => {
                  await requirePerm(perms.analyticsStats)(ctx);
                  return prefetchAnalyticsStatsData(ctx);
                },
              },
              {
                path: 'analytics/:filter/:linkId?',
                element: <Analytics />,
                loader: analyticsLoader,
              },
              {
                path: 'insights/:insightPath',
                element: <Insights />,
                loader: requirePerm(perms.insights),
              },
              {
                path: 'billing',
                children: [
                  {
                    index: true,
                    element: <Billing />,
                    loader: requirePerm(perms.billing),
                  },
                  {
                    path: ':invoiceId',
                    element: <InvoiceRoute />,
                    loader: requirePerm(perms.invoiceRoute),
                  },
                ],
              },
              {
                path: 'publisher-payment-history',
                element: <PublisherPaymentHistory />,
                loader: requirePerm(perms.publisherPaymentHistory),
              },
              {
                path: 'publisher-ads',
                element: <PublisherAds />,
                loader: requirePerm(perms.publisherAds),
              },
              {
                path: 'poll-manager',
                loader: requirePerm(perms.polls),
                children: [
                  {
                    index: true,
                    element: <PollManager />,
                    loader: async (ctx) => {
                      await requirePerm(perms.polls)(ctx);
                      return prefetchAdManagerData(ctx);
                    },
                    shouldRevalidate: ignoreSearchParams,
                  },
                  {
                    path: ':campaignId',
                    children: [
                      {
                        path: 'edit',
                        element: <EditingAdFormHandler />,
                        loader: async (ctx) => {
                          if (!ctx.params.campaignId) {
                            throw redirect(
                              `/${ctx.params.accountType}/${ctx.params.accountId}/poll-manager`,
                            );
                          }
                          await requirePerm(perms.polls)(ctx);
                          return prefetchGenericAdFormData(ctx);
                        },
                        shouldRevalidate: ignoreSearchParams,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'celtra-reports',
                element: <CeltraReports />,
                loader: requirePerm(perms.celtraReports),
              },
              {
                path: 'dappier-reports',
                element: <DappierReports />,
                loader: requirePerm(perms.dappierReports),
              },
              {
                path: 'new-client',
                element: <NewClient />,
                loader: requirePerm(perms.newClient),
              },
              {
                path: 'getting-started',
                element: <GettingStarted />,
                loader: requirePerm(perms.loggedIn),
              },
              {
                path: 'forbidden',
                element: <ForbiddenPage />,
              },
              {
                path: 'accounts-archived',
                element: <ArchivedAccountsPage />,
              },
              {
                path: 'account-entry-fallback',
                element: <AccountEntryFallback />,
              },
              {
                path: '*',
                element: <ErrorPage />,
              },
            ],
          },
          {
            path: '*',
            element: <ErrorPage />,
          },
        ],
      },
      {
        element: <UserProfileLayout />,
        children: [
          {
            path: 'user-profile',
            element: <UserProfile />,
          },
        ],
      },
      {
        path: 'staff',
        element: <StaffLayout />,
        middleware: [resolveStaffSessionMiddleware(globalClient)],
        children: [
          { index: true, element: <Navigate to="home" /> },
          {
            path: 'home',
            element: <StaffDashboard />,
          },
          {
            path: 'repricingVoidingStatus/:jobId',
            element: <RepricingVoidingStatus />,
          },
          {
            path: 'repricingvoiding',
            element: <RepricingVoiding />,
          },
          {
            path: 'void-reprice-form',
            element: <VoidRepriceForm />,
            loader: requirePerm(perms.isDianomi),
          },
          {
            path: 'voiding-repricing-manager',
            loader: () => {
              throw redirect('../void-reprice-manager');
            },
          },
          {
            path: 'void-reprice-manager',
            element: <VoidRepriceManagerTable />,
            loader: requirePerm(perms.isDianomi),
          },
          {
            path: 'void-reprice-control-panel',
            element: <VoidRepriceControlPanel />,
            loader: requirePerm(perms.isDianomi),
          },
          {
            path: 'select-account',
            element: <OnLoginClientsSelectorHandler />,
          },
          {
            path: 'ops',
            children: [
              {
                index: true,
                loader: () => {
                  // no base route here, just redirect to the column metadata page
                  throw redirect('/staff/ops/analytics/column-metadata');
                },
              },
              {
                path: 'analytics/column-metadata',
                element: <ColumnTooltipsPage />,
                loader: prefetchColumnTooltipsLoader(globalClient),
              },
              {
                path: 'analytics/report-templates',
                element: <ReportTemplatesListPage />,
                loader: prefetchStaffReportTemplatesLoader(globalClient),
              },
              {
                path: 'analytics/report-templates/new/:statKey',
                element: <ReportTemplateEditorPage />,
                loader: prefetchStaffReportTemplatesLoader(globalClient),
              },
              {
                path: 'analytics/report-templates/edit/:templatePublicId',
                element: <ReportTemplateEditorPage />,
                loader: prefetchStaffReportTemplatesLoader(globalClient),
              },
            ],
          },
          {
            path: 'eng',
            children: [
              {
                index: true,
                loader: () => {
                  throw redirect('/staff/eng/update-request');
                },
              },
              {
                path: 'update-request',
                element: <RequestAnalyticsUpdatePage />,
                loader: prefetchStaffAnalyticsUpdateRequestLoader(globalClient),
              },
            ],
          },
          {
            // only supports clients right now, but leaving the path as :accountType for clarity
            path: ':accountType',
            element: <StaffClientManagerLayout />,
            loader: staffClientPermsLoader(globalClient),
            children: [
              {
                index: true,
                element: <StaffClientHomepage />,
              },
            ],
            handle: {
              lazyChildren: () => import('~/views/Staff/StatLimits/routes').then((m) => m.children),
            },
          },
          {
            path: 'client/online-signup',
            children: [
              { index: true, element: <Navigate to="invite-new-client" /> },
              {
                path: 'invite-new-client',
                element: <InviteClient />,
              },
              {
                path: 'manage-invites',
                element: <ManageInvites />,
              },
              {
                path: 'upload-io',
                element: <UploadSignupIOWrapper />,
              },
            ],
          },
          {
            path: '*',
            element: <ErrorPage />,
          },
        ],
      },
      {
        path: '*',
        element: <ErrorPage />,
      },
      {
        element: <AuthLayout />,
        children: [
          { path: 'no-session', element: <NoSessionPage /> },

          {
            path: 'login',
            element: <LoginPage />,
          },
          {
            path: 'setup-mfa',
            element: <SetupMFAPage />,
          },
          {
            path: 'account-recovery',
            element: <MFARecoveryPage />,
          },
          {
            path: 'auth/email-mfa',
            element: <MfaPage />,
            loader: async (ctx) => {
              const url = new URL(ctx.request.url);

              const token = url.searchParams.get('token');
              if (!token) {
                return {
                  message: 'No token provided',
                };
              }
              const res = await customFetch.post<
                { success: true } | { success: false; message: string }
              >('/auth/email-mfa', JSON.stringify({ token }), {
                useNodeEndpoint: true,
                throwOnHTTPError: false,
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              const referer = url.searchParams.get('referer');

              if (res.status === 200 || res.data.success) {
                //
                const cookies = new Cookies();
                cookies.set('SESSION_STARTED', 1, { path: '/', secure: true });
                throw redirect(`/${referer ? `?referer=${referer}` : ''}`);
              }

              return {
                message: res.data.message,
              };
            },
          },
          {
            path: 'forgot-password',
            element: <SetPassword />,
          },
          {
            path: 'set-password',
            element: <SetPassword />,
          },
          {
            path: '*',
            element: <ErrorPage />,
          },
          {
            path: 'legal',
            element: <Legal />,
            children: [
              {
                index: true,
                element: <Navigate to="marketing-terms/us" replace />,
              },
              {
                path: 'marketing-terms',
                element: <Navigate to="us" replace />,
              },
              {
                path: ':legalType',
                element: <LegalType />,
              },
              {
                path: ':legalType/:region',
                element: <LegalType />,
              },
            ],
          },
          {
            path: 'invite/',
            children: [
              {
                index: true,
                element: <Navigate to="invalid-invite" />,
              },
              {
                path: ':inviteId',
                element: <Invite />,
                children: [
                  {
                    path: 'step',
                    element: <StepLayout />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="user" />,
                      },
                      {
                        path: 'user',
                        element: <UserFormHandler />,
                      },
                      {
                        path: 'company-details',
                        element: <ClientDetails />,
                      },
                      {
                        path: 'billing-details',
                        element: <BillingDetails />,
                      },
                      {
                        path: 'confirm',
                        element: <ReviewStep />,
                      },
                      {
                        path: '*',
                        element: <ErrorPage />,
                      },
                    ],
                  },
                  {
                    path: 'finish',
                    element: <SignUpCompletePage />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <ErrorPage />,
  },
];
